import { DateTime } from "luxon";

import { getPlanLabel } from "@app/analytics/account/Billing/utils";
import { UNKNOWN_PLAN } from "@app/common/constants/billing";
import type {
  BillingCurrentPlan,
  BillingPlan,
  BillingScheduledPlan,
  BillingSubscription,
  User,
} from "@app/common/types";
import { optionalParseUTCDateTimeFromString } from "@app/utils/utils";

export const getRemainingTrialDays = (
  trialEndDate?: string,
  now: DateTime = DateTime.now()
): number => {
  const trialEnd = optionalParseUTCDateTimeFromString(trialEndDate);
  if (!trialEnd) return 0;

  const numDays = trialEnd.diff(now, "days").as("days");
  return Math.max(0, Math.round(numDays));
};

export const planIsUnknown = (plan: BillingPlan) => plan === "unknown";

export const getCurrentPlanFromSubscriptionAndUser = (
  subscription: BillingSubscription,
  user: User | null
): BillingCurrentPlan => {
  if (planIsUnknown(subscription.plan)) {
    const billingAccountPlan = user?.billingAccount
      ? user.billingAccount.plan
      : user
        ? "free" // if there is a user but no billing account, this user is on the free plan
        : null;

    if (billingAccountPlan) {
      // if we're having trouble getting the subscription from the BE, use the plan
      // from the user's billing account as a fallback. this will always be the same
      // plan that's returned on the subscription (though is more likely to be stale, as we
      // don't fetch the user's billing account as frequently as we do the subscription).
      // we also won't know the cadence (if the user is on a paid plan) or the actual seat
      // maximum, so we will assume the user is at their seat maximum until we can
      // definitively determine from the BE (via their subscription) that they are not

      return {
        type: billingAccountPlan,
        label: getPlanLabel(billingAccountPlan),
        seatMaximum: user?.billingAccount?.billableSeats ?? 1,
      };
    }

    return UNKNOWN_PLAN;
  }

  return {
    type: subscription.plan,
    billingCadence: subscription.billingCadence,
    label: getPlanLabel(subscription.plan),
    seatMaximum: subscription.settings.workspaceMemberLimit,
    stripePriceId: subscription.stripePriceId,
  };
};

export const getScheduledPlanFromSubscription = (
  subscription: BillingSubscription
): BillingScheduledPlan | undefined => {
  if (!subscription.scheduledUpdate) return;

  return {
    type: subscription.scheduledUpdate.plan,
    label: getPlanLabel(subscription.scheduledUpdate.plan),
    billingCadence: subscription.scheduledUpdate.billingCadence,
    stripePriceId: subscription.scheduledUpdate.stripePriceId,
  };
};
